/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, useRouter, useTranslations } from "@i18n/next";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import type { GiftDetails } from "@packages/sdk";
import { useBranch, useRequestMe, useSession } from "@packages/sdk";
import { URL_GIFT_IMAGE } from "@packages/sdk/src/lib/constants/urls/urlGift.constants";
import type { ThemeType } from "@packages/ui/emotion";
import {
  Button,
  createModal,
  cssMediaQueryIsSafari,
  cssMediaQueryMinWidthTablet,
  Modal,
  ModalHeader,
  numericPercentages,
  numericViewportHeight,
  numericViewportWidth,
  radius,
  ShareIcon,
  ShareModal,
  SimplifiedAuth,
  spacing,
  stroke,
  Text,
  useModal,
} from "@packages/ui/emotion";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import Image from "next/image";
import { useMemo } from "react";

import { europeanStyleName } from "../../lib";
import Alert from "./assets/alert.png";
import Anon from "./assets/anonymous.png";
import Default from "./assets/default.png";
import ErrorIllo from "./assets/error.png";
import GiftIllo from "./assets/gift.png";
import GiftIcon from "./assets/icon.png";
import { GiftDetail } from "./GiftDetail";

const headingsStyle: Interpolation = {
  textAlign: "center",
};
const subheadingStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});
const modalStyle: Interpolation = {
  alignContent: "center",
  alignItems: "center",
  borderRadius: radius.none,
  display: "grid",
  gap: spacing.m,
  gridAutoRows: "min-content",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "min-content",
  height: numericViewportHeight[100],
  inset: spacing.none,
  justifyContent: "center",
  maxWidth: 480,
  overflow: "auto",
  padding: spacing.l,
  transform: "none",
  width: numericViewportWidth[100],
  [cssMediaQueryMinWidthTablet]: {
    alignContent: "center",
    borderRadius: radius.l,
    height: "max-content",
    inset: numericPercentages[50],
    transform: "translate(-50%, -50%)",
    [cssMediaQueryIsSafari]: {
      alignContent: "start",
    },
  },
};
const imageStyle: Interpolation = {
  height: "auto",
  maxHeight: 200,
  objectFit: "contain",
  objectPosition: "center",
  width: spacing.full,
};
const detailSectionStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.l,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  display: "flex",
  flexDirection: "column",
  gap: spacing.none,
  justifyContent: "flex-start",
  padding: spacing.s,
  width: spacing.full,
});
const authStyle: Interpolation = {
  padding: spacing.none,
};

const SCREEN = "gift_sub_received";

export const GiftRecipientModal = createModal(
  ({
    giftDetails,
    promoCode,
  }: {
    giftDetails: GiftDetails;
    promoCode: string;
  }) => {
    const modal = useModal();
    const t = useTranslations();
    const router = useRouter();
    const { status } = useSession();
    const { query: me } = useRequestMe();
    const shareModal = useModal(ShareModal);
    const { gift } = useBranch();
    const { track } = useAnalytics();
    useViewedScreen({
      screen:
        status === "authenticated"
          ? SCREEN
          : "gift_sub_received_unauthenticated",
      properties: {
        is_code_redeemable: giftDetails?.redemption_error?.code !== 3047,
        is_user_eligible: giftDetails?.redemption_error?.code !== 3003,
      },
      isReady: !!giftDetails && modal.visible && status !== "loading",
    });

    const gifter = useMemo(() => {
      const composed = europeanStyleName(giftDetails.purchaser);
      if (composed.length) return composed;
      return t("gift_sub_a_generous_user");
    }, [giftDetails.purchaser]);

    const recipient = useMemo(() => {
      if (!me.data) return null;
      const composed = europeanStyleName(me.data);
      if (composed.length) return composed;
      return null;
    }, [me.data]);

    const handleRegift = async () => {
      track({
        event: HallowAnalyticsEvent.TappedRegiftGiftSubscription,
        properties: {
          screen_name: SCREEN,
        },
      });
      await shareModal.show({
        imgSrc: URL_GIFT_IMAGE,
        title: t("gift_sub_share_title"),
        link: await gift({ promoCode }),
        description: promoCode,
        color: "#7166D2",
      });
      modal.resolve(false);
      modal.remove();
      router.push("/home");
    };

    return (
      <Modal
        open={modal.visible}
        onOpenChange={(open) => {
          if (open) modal.show();
          else {
            modal.resolve(false);
            modal.remove();
          }
        }}
        contentProps={{ onInteractOutside: (e) => e.preventDefault() }}
        overlayProps={{
          onClick: () => {
            modal.resolve(false);
            modal.remove();
            router.push(status === "authenticated" ? "/home" : "/");
          },
        }}
        contentCSS={modalStyle}
        showClose
      >
        <VisuallyHidden>
          <ModalHeader>{t("you_got_a_gift")}</ModalHeader>
        </VisuallyHidden>

        {giftDetails.redemption_error?.code === 3047 ? (
          <>
            <Image
              src={ErrorIllo}
              alt={""}
              placeholder={"blur"}
              css={imageStyle}
            />
            <Text as={"h1"} type={"title"} size={"xl"} css={headingsStyle}>
              {t("gift_hallow_already_redeemed")}
            </Text>
            <Button
              asChild
              isFullWidth
              size={"l"}
              onClick={() => {
                modal.resolve(false);
                modal.remove();
              }}
            >
              <Link href={status === "authenticated" ? "/home" : "/"}>
                {t("web_close_button_text")}
              </Link>
            </Button>
          </>
        ) : (
          <>
            <Image
              src={GiftIllo}
              alt={""}
              placeholder={"blur"}
              css={imageStyle}
            />
            <Text type={"title"} size={"xl"} as={"h1"} css={headingsStyle}>
              {t("you_got_a_gift")}
            </Text>
            {!giftDetails.redemption_error?.code && (
              <Text
                type={"body"}
                size={"m"}
                as={"p"}
                css={[headingsStyle, subheadingStyle]}
              >
                {t("gift_sub_redeem_subtitle_num_days", {
                  "0": giftDetails.trial_days,
                })}
              </Text>
            )}
            <div css={detailSectionStyle}>
              <GiftDetail imageSrc={GiftIcon} text={giftDetails.name} />
              <GiftDetail
                imageSrc={giftDetails.purchaser?.image_url ?? Anon}
                text={gifter}
                label={t("sent_by")}
              />
              {status === "authenticated" && (
                <GiftDetail
                  imageSrc={me.data?.image_url ?? Default}
                  text={recipient ?? me.data?.email ?? me.data?.phone ?? ""}
                  label={t("signed_in_as")}
                  detail={
                    recipient?.length
                      ? (me.data?.email ?? me.data?.phone ?? "")
                      : null
                  }
                />
              )}
              {giftDetails.redemption_error?.code === 3003 && (
                <GiftDetail
                  imageSrc={Alert}
                  text={t("gift_sub_already_recurring")}
                  variant={"error"}
                />
              )}
            </div>
            {status === "authenticated" ? (
              giftDetails.redemption_error?.code === 3003 ? (
                <Button
                  isFullWidth
                  startIcon={<ShareIcon />}
                  size={"l"}
                  onClick={handleRegift}
                >
                  {t("gift_sub_regift")}
                </Button>
              ) : (
                <Button
                  isFullWidth
                  size={"l"}
                  onClick={() => {
                    track({
                      event: HallowAnalyticsEvent.TappedRedeemGiftSubscription,
                      properties: {
                        screen_name: SCREEN,
                      },
                    });
                    modal.resolve(true);
                    modal.remove();
                  }}
                >
                  {t("gift_sub_redeem_gift")}
                </Button>
              )
            ) : (
              <SimplifiedAuth
                onFlowChange={(flow) => {
                  // specifically don't resolve the modal in this flow, or the redirects will fight
                  modal.remove();
                  router.push(
                    `/login?flow=${flow}&redirect=${encodeURIComponent(`/gift/${promoCode}`)}`,
                  );
                }}
                onAuth={() => {
                  modal.resolve(true);
                  modal.remove();
                }}
                css={authStyle}
              />
            )}
          </>
        )}
      </Modal>
    );
  },
);
